import { useEffect, useState } from 'react';

import { Link, useSearchParams } from 'react-router-dom';

import { Button, showSnackbar } from 'fluxo-ui';
import { LoadingIcon } from 'fluxo-ui/icons';

import { AuthShell, StatusCircle } from '../components/common';
import { ROUTES } from '../common/constants';
import authService from '../services/auth.service';

const VerifyEmailPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (token) {
            verifyEmail(token);
        } else {
            setStatus('error');
            setErrorMessage('No verification token provided. Please check your email link.');
        }
    }, [token]);

    const verifyEmail = async (verificationToken: string) => {
        try {
            await authService.verifyEmail(verificationToken);
            setStatus('success');
            showSnackbar('Email verified successfully!', 'Success', { type: 'success' });
        } catch (err: any) {
            setStatus('error');
            setErrorMessage(err.message || 'Failed to verify email. The link may have expired.');
        }
    };

    if (status === 'verifying') {
        return (
            <AuthShell>
                <div className="text-center">
                    <LoadingIcon className="w-12 h-12 animate-spin text-teal-600 dark:text-teal-400 mx-auto mb-5" />
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Verifying your email</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Please wait while we verify your email address...</p>
                </div>
            </AuthShell>
        );
    }

    if (status === 'success') {
        return (
            <AuthShell>
                <div className="text-center">
                    <StatusCircle tone="success" className="mx-auto mb-5" />
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Email verified!</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                        Your email has been verified successfully. You can now log in to your account.
                    </p>
                    <Link to={ROUTES.LOGIN}>
                        <Button label="Go to Login" variant="primary" className="w-full" />
                    </Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <div className="text-center">
                <StatusCircle tone="error" className="mx-auto mb-5" />
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Verification failed</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{errorMessage}</p>
                <div className="space-y-3">
                    <Link to={ROUTES.SIGNUP} className="block">
                        <Button label="Sign Up Again" variant="default" layout="outlined" className="w-full" />
                    </Link>
                    <Link to={ROUTES.LOGIN} className="block">
                        <Button label="Go to Login" variant="primary" className="w-full" />
                    </Link>
                </div>
            </div>
        </AuthShell>
    );
};

export default VerifyEmailPage;
